import { useSuspenseQuery } from '@apollo/client'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { FetchGoalConfigsDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath, useCanForView } from '@salescore/client-common'
import type { ViewConfigKpiPivot } from '@salescore/core'
import { Array, Order, pipe } from 'effect'
import { type ReactElement, useMemo } from 'react'

import { GoalConfigsPicker } from './GoalConfigsPicker'
import { KpiPivotPresetsPicker } from './KpiPivotPresetsPicker'

export const PickersForKpiPivotView = ({
  view,
  withDateField,
  config,
  hasChangeToKpiPivotParameter,
  setHasChangeToViewSchema,
}: {
  withDateField: boolean
  view: ViewFieldsFragment
  config: ViewConfigKpiPivot
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const canUpdate = useCanForView('update', view)

  const {
    data: { goalConfigs },
    refetch,
  } = useSuspenseQuery(FetchGoalConfigsDocument, {
    variables: { organizationId: getOrganizationIdFromPath(), viewId: view.id },
    fetchPolicy: 'cache-and-network',
  })
  const sortedGoalConfigs = useMemo(
    () => pipe(goalConfigs, Array.sortBy(Order.mapInput(Order.Number, (x) => x.rank))),
    [goalConfigs],
  )

  return (
    <>
      <KpiPivotPresetsPicker
        canUpdate={canUpdate}
        withNoDateFieldKpis={!withDateField}
        view={view}
        config={config}
        hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
        goalConfigs={sortedGoalConfigs}
      />
      <GoalConfigsPicker
        canUpdate={canUpdate}
        viewId={view.id}
        goalConfigs={sortedGoalConfigs}
        refetchGoalConfigs={() => {
          void refetch()
        }}
      />
    </>
  )
}
